import { Box, Container, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Globe, CheckSquare, AlertCircle, Building2, Phone } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const sections = [
  {
    icon: Globe,
    number: '01',
    title: 'Our Approach to Web Accessibility',
    content: `Our goal is to provide a seamless and inclusive browsing experience for all visitors, including those with disabilities. We strive to adhere to the Web Content Accessibility Guidelines (WCAG) [2.0 / 2.1 / 2.2] to ensure our website is perceivable, operable, understandable, and robust for all users.`,
  },
  {
    icon: CheckSquare,
    number: '02',
    title: 'Accessibility Features of Our Website',
    content: `Clarence Homes has implemented various accessibility features to ensure an inclusive online environment. Our website is designed to work effectively with assistive technologies, such as screen readers and keyboard navigation. We have also incorporated clear heading structures, alternative text for images, and compliant color combinations to support accessibility.\n\nIn addition, we have minimized the use of motion, made all multimedia content accessible, and optimized the content order to improve navigation for individuals with disabilities. Our commitment to accessibility is further demonstrated through periodic assessments and enhancements.`,
  },
  {
    icon: AlertCircle,
    number: '03',
    title: 'Declaration of Partial Compliance',
    content: `Certain pages on our site may contain content from third parties, impacting the level of accessibility. We acknowledge this challenge and are actively working to address these issues to ensure a fully inclusive digital experience for all users.`,
  },
  {
    icon: Building2,
    number: '04',
    title: 'Physical Accessibility Arrangements',
    content: `At Clarence Homes, we prioritize physical accessibility in our offices and branches. Our facilities are equipped with a range of accessibility arrangements, including designated parking spaces, ramps, and accessible amenities. We are committed to providing a welcoming and accommodating environments for all individuals.`,
  },
];

function Accessibility() {
  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f8f8', overflow: 'hidden' }}>

      {/* Hero */}
      <Box sx={{
        backgroundColor: 'var(--primary-color)',
        py: { xs: 7, md: 11 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          top: '-80px', right: '-80px',
          width: '320px', height: '320px',
          borderRadius: '50%',
          border: '70px solid rgba(192,174,93,0.07)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""', position: 'absolute',
          bottom: '-90px', left: '-50px',
          width: '280px', height: '280px',
          borderRadius: '50%',
          border: '55px solid rgba(192,174,93,0.05)',
          pointerEvents: 'none',
        },
      }}>
        <Typography sx={{ fontSize: { xs: '32px', md: '52px' }, fontWeight: 800, color: '#fff', letterSpacing: 2, lineHeight: 1.1 }}>
          ACCESSIBILITY STATEMENT
        </Typography>
        <Box sx={{ width: '80px', height: '4px', backgroundColor: 'var(--primary-text-color)', mx: 'auto', mt: 3, mb: 2.5 }} />
        <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, color: 'rgba(255,255,255,0.6)', maxWidth: 560, mx: 'auto', lineHeight: 1.8 }}>
          Clarence Homes is dedicated to ensuring equal access and opportunity for individuals with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to enhance accessibility for all.
        </Typography>
      </Box>

      {/* Gold bar */}
      <Box sx={{ backgroundColor: 'var(--primary-text-color)', py: 2 }}>
        <Container maxWidth="xl">
          <Typography sx={{ fontSize: '13px', color: 'var(--primary-color)', fontWeight: 700, textAlign: 'center' }}>
            We are committed to WCAG 2.0 / 2.1 / 2.2 compliance — building an inclusive digital experience for all users.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {/* Section cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Box
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 70}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 20px rgba(19,25,83,0.07)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                    transform: 'translateY(-6px)',
                  }
                }}
              >
                {/* Left navy strip */}
                <Box sx={{
                  width: { xs: '100%', sm: '90px' },
                  backgroundColor: 'var(--primary-color)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  py: { xs: 2.5, sm: 0 }, gap: 1.5, flexShrink: 0,
                }}>
                  <Icon size={26} color="var(--primary-text-color)" />
                  <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'rgba(255,255,255,0.18)', lineHeight: 1 }}>
                    {section.number}
                  </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 3, md: 4.5 }, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: '4px', height: '22px', backgroundColor: 'var(--primary-text-color)', borderRadius: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 700, color: 'var(--primary-color)' }}>
                      {section.title}
                    </Typography>
                  </Box>
                  {section.content.split('\n\n').map((para, i) => (
                    <Typography key={i} sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95, mb: i < section.content.split('\n\n').length - 1 ? 2 : 0 }}>
                      {para}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Contact card */}
        <Box
          data-aos="fade-up"
          sx={{
            mt: 5,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(19,25,83,0.12)',
          }}
        >
          {/* Card header */}
          <Box sx={{
            backgroundColor: 'var(--primary-color)',
            px: { xs: 3, md: 5 }, py: 3,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '10px',
              backgroundColor: 'rgba(192,174,93,0.15)',
              border: '1.5px solid var(--primary-text-color)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Phone size={22} color="var(--primary-text-color)" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', color: 'var(--primary-text-color)', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
                05 — Get In Touch
              </Typography>
              <Typography sx={{ fontSize: { xs: '18px', md: '22px' }, fontWeight: 800, color: '#fff' }}>
                Contact Us for Accessibility Support
              </Typography>
            </Box>
          </Box>

          {/* Card body */}
          <Box sx={{ backgroundColor: '#fff', px: { xs: 3, md: 5 }, py: 4 }}>
            <Typography sx={{ fontSize: '15px', color: '#555', lineHeight: 1.95, mb: 3.5 }}>
              If you encounter any accessibility barriers on our website or have specific accessibility-related inquiries, please reach out to our dedicated accessibility coordinator. You can contact Dipak Nayak at{' '}
              <Box component="a" href="tel:1300451780" sx={{ color: 'var(--primary-color)', fontWeight: 700, textDecoration: 'none', '&:hover': { color: 'var(--primary-text-color)' } }}>1300 451 780</Box>
              {' '}or via email at{' '}
              <Box component="a" href="mailto:info@clarencehomes.com.au" sx={{ color: 'var(--primary-color)', fontWeight: 700, textDecoration: 'none', '&:hover': { color: 'var(--primary-text-color)' } }}>info@clarencehomes.com.au</Box>
              . We value your feedback and are dedicated to addressing any accessibility concerns promptly.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { label: 'Coordinator', value: 'Dipak Nayak', href: null },
                { label: 'Phone', value: '1300 451 780', href: 'tel:1300451780' },
                { label: 'Email', value: 'info@clarencehomes.com.au', href: 'mailto:info@clarencehomes.com.au' },
              ].map(({ label, value, href }) => (
                <Box key={label} sx={{
                  flex: '1 1 180px',
                  p: 2.5,
                  borderRadius: '8px',
                  backgroundColor: '#f8f8f8',
                  border: '1.5px solid rgba(19,25,83,0.08)',
                  ...(href && { cursor: 'pointer', '&:hover': { borderColor: 'var(--primary-text-color)', backgroundColor: '#fff' } })
                }}
                  component={href ? 'a' : 'div'}
                  href={href || undefined}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography sx={{ fontSize: '11px', color: 'var(--primary-text-color)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'var(--primary-color)' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default Accessibility;
